/**
 * Sync Services Settings Component
 *
 * Admin UI for managing OData sync service configurations per Connection.
 * Shows which sync services are registered, their status, and allows
 * CRUD operations + triggering individual syncs.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  RefreshCw,
  Plus,
  Trash2,
  Play,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Settings,
  Database,
  ToggleLeft,
  ToggleRight,
  Loader2,
  Download,
  Eye,
  X,
  Pencil,
  Save,
} from 'lucide-react';
import {
  syncServicesApi,
  connectionsApi,
  type SyncServiceConfig,
  type AvailableSyncService,
} from '../../api/client';

// ============================================================================
// Status Badge Component
// ============================================================================

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    success: 'bg-green-50 text-green-700 border-green-200',
    failed: 'bg-red-50 text-red-700 border-red-200',
    running: 'bg-blue-50 text-blue-700 border-blue-200',
    partial: 'bg-amber-50 text-amber-700 border-amber-200',
    '': 'bg-gray-50 text-gray-500 border-gray-200',
  };

  const icons: Record<string, React.ReactNode> = {
    success: <CheckCircle2 className="w-3 h-3" />,
    failed: <XCircle className="w-3 h-3" />,
    running: <Loader2 className="w-3 h-3 animate-spin" />,
    partial: <AlertTriangle className="w-3 h-3" />,
    '': <Clock className="w-3 h-3" />,
  };

  const label = status || 'Never synced';

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles['']}`}>
      {icons[status] || icons['']}
      {label.charAt(0).toUpperCase() + label.slice(1)}
    </span>
  );
}

// ============================================================================
// Relative Time Helper
// ============================================================================

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ============================================================================
// Main Component
// ============================================================================

export function SyncServicesSettings() {
  const queryClient = useQueryClient();
  const [selectedConnectionId, setSelectedConnectionId] = useState<string>('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerTypes, setRegisterTypes] = useState<Set<string>>(new Set());

  // Fetch connections
  const { data: connections } = useQuery({
    queryKey: ['connections'],
    queryFn: connectionsApi.list,
  });

  // Fetch sync service configs for selected connection
  const { data: configs, isLoading: configsLoading, isFetching: configsFetching } = useQuery({
    queryKey: ['sync-services', selectedConnectionId],
    queryFn: () => syncServicesApi.list(selectedConnectionId || undefined),
    enabled: true,
  });

  // Fetch available services (from Python code)
  const { data: availableData } = useQuery({
    queryKey: ['sync-services-available'],
    queryFn: syncServicesApi.available,
  });

  // Mutations
  const registerMutation = useMutation({
    mutationFn: ({ connectionId, types }: { connectionId: string; types: string[] }) =>
      syncServicesApi.register(connectionId, types),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sync-services'] });
      setShowRegisterModal(false);
      setRegisterTypes(new Set());
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => syncServicesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sync-services'] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      syncServicesApi.update(id, { is_enabled: enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sync-services'] });
    },
  });

  const [syncFeedback, setSyncFeedback] = useState<{
    id: string;
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set());

  // View Data modal state
  const [viewingConfig, setViewingConfig] = useState<SyncServiceConfig | null>(null);
  const [viewData, setViewData] = useState<Record<string, unknown>[] | null>(null);
  const [viewDataLoading, setViewDataLoading] = useState(false);
  const [viewDataTotal, setViewDataTotal] = useState(0);

  const handleViewData = async (config: SyncServiceConfig) => {
    setViewingConfig(config);
    setViewDataLoading(true);
    setViewData(null);
    try {
      const result = await syncServicesApi.viewData(config.id, { page_size: 5000 });
      setViewData(result.data);
      setViewDataTotal(result.total_count);
    } catch (error) {
      console.error('Failed to load data:', error);
      setViewData([]);
      setViewDataTotal(0);
    } finally {
      setViewDataLoading(false);
    }
  };

  // Edit modal state
  const [editingConfig, setEditingConfig] = useState<SyncServiceConfig | null>(null);
  const [editForm, setEditForm] = useState({
    odata_path: '',
    batch_size: 500,
    delta_sync_enabled: true,
    sync_interval_minutes: 0,
  });

  const openEditModal = (config: SyncServiceConfig) => {
    setEditingConfig(config);
    setEditForm({
      odata_path: config.odata_path || '',
      batch_size: config.batch_size,
      delta_sync_enabled: config.delta_sync_enabled,
      sync_interval_minutes: config.sync_interval_minutes,
    });
  };

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; updates: Partial<SyncServiceConfig> }) =>
      syncServicesApi.update(data.id, data.updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sync-services'] });
      setEditingConfig(null);
    },
  });

  const syncNowMutation = useMutation({
    mutationFn: ({ id, delta }: { id: string; delta: boolean }) => {
      setSyncingIds((prev) => new Set(prev).add(id));
      return syncServicesApi.syncNow(id, delta);
    },
    onSuccess: (data, variables) => {
      setSyncFeedback({
        id: variables.id,
        type: 'success',
        message: `Sync queued for ${data.entity_type} (Task: ${data.task_id.slice(0, 8)}...)`,
      });
      queryClient.invalidateQueries({ queryKey: ['sync-services'] });
      setTimeout(() => setSyncFeedback(null), 4000);

      // Poll for updated results after Celery task completes
      const pollIntervals = [3000, 6000, 12000, 20000];
      pollIntervals.forEach((delay) => {
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['sync-services'] });
          // Remove spinner once we see updated data (after first poll)
          if (delay === pollIntervals[0]) {
            setSyncingIds((prev) => {
              const next = new Set(prev);
              next.delete(variables.id);
              return next;
            });
          }
        }, delay);
      });
    },
    onError: (error: Error, variables) => {
      setSyncingIds((prev) => {
        const next = new Set(prev);
        next.delete(variables.id);
        return next;
      });
      setSyncFeedback({
        id: variables.id,
        type: 'error',
        message: `Sync failed: ${error.message}`,
      });
      setTimeout(() => setSyncFeedback(null), 5000);
    },
  });

  // Toggle row expansion
  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Filter configs by selected connection
  const filteredConfigs = configs?.filter(
    (c) => !selectedConnectionId || c.connection === selectedConnectionId
  ) || [];

  // Determine which entity types are already registered for the selected connection
  const registeredTypes = new Set(
    filteredConfigs.map((c) => c.entity_type)
  );

  // Available but not yet registered services
  const unregisteredServices = (availableData?.services || []).filter(
    (s) => !registeredTypes.has(s.entity_type)
  );

  // Stats
  const totalConfigs = filteredConfigs.length;
  const enabledConfigs = filteredConfigs.filter((c) => c.is_enabled).length;
  const failedConfigs = filteredConfigs.filter((c) => c.last_sync_status === 'failed').length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Sync Services
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage OData sync service configurations per connection
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['sync-services'] });
              queryClient.invalidateQueries({ queryKey: ['sync-services-available'] });
            }}
            disabled={configsFetching}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${configsFetching ? 'animate-spin' : ''}`} />
            {configsFetching ? 'Loading...' : 'Refresh'}
          </button>
          {selectedConnectionId && unregisteredServices.length > 0 && (
            <button
              onClick={() => setShowRegisterModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf]"
            >
              <Plus className="w-4 h-4" />
              Register Services
            </button>
          )}
        </div>
      </div>

      {/* Connection Filter */}
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="p-2 bg-white rounded-lg shadow-sm">
            <Database className="w-5 h-5 text-cyan-600" />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Filter by Connection
            </label>
            <select
              value={selectedConnectionId}
              onChange={(e) => setSelectedConnectionId(e.target.value)}
              className="w-full max-w-md px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            >
              <option value="">All Connections</option>
              {connections?.map((conn) => (
                <option key={conn.id} value={conn.id}>
                  {conn.name} {conn.is_active ? '' : '(Inactive)'}
                </option>
              ))}
            </select>
          </div>

          {/* Quick Stats */}
          {totalConfigs > 0 && (
            <div className="flex items-center gap-4 text-sm">
              <div className="text-center">
                <div className="text-lg font-semibold text-gray-900">{totalConfigs}</div>
                <div className="text-xs text-gray-500">Total</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-green-600">{enabledConfigs}</div>
                <div className="text-xs text-gray-500">Active</div>
              </div>
              {failedConfigs > 0 && (
                <div className="text-center">
                  <div className="text-lg font-semibold text-red-600">{failedConfigs}</div>
                  <div className="text-xs text-gray-500">Failed</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Loading State */}
      {configsLoading ? (
        <div className="animate-pulse space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded-lg" />
          ))}
        </div>
      ) : filteredConfigs.length === 0 ? (
        /* Empty State */
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <Settings className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No sync services configured</p>
          <p className="text-sm text-gray-400 mt-1">
            {selectedConnectionId
              ? 'Click "Register Services" to add sync services for this connection'
              : 'Select a connection above, then register sync services'}
          </p>
          {selectedConnectionId && (
            <button
              onClick={() => {
                // Register all at once
                setRegisterTypes(new Set(['all']));
                registerMutation.mutate({
                  connectionId: selectedConnectionId,
                  types: ['all'],
                });
              }}
              disabled={registerMutation.isPending}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {registerMutation.isPending ? 'Registering...' : 'Register All Services'}
            </button>
          )}
        </div>
      ) : (
        /* Services Table */
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {/* Table Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="w-5 shrink-0"></div>
            <div className="flex-1 min-w-0">Service</div>
            <div className="w-24 shrink-0">Status</div>
            <div className="w-20 shrink-0">Last Sync</div>
            <div className="w-16 shrink-0 text-center">Enabled</div>
            <div className="w-32 shrink-0 text-right">Actions</div>
          </div>

          {/* Table Body */}
          {filteredConfigs.map((config) => (
            <div key={config.id} className="border-b border-gray-100 last:border-b-0">
              {/* Main Row */}
              <div
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                onClick={() => toggleRow(config.id)}
              >
                {/* Expand Icon */}
                <div className="w-5 shrink-0 text-gray-400">
                  {expandedRows.has(config.id) ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </div>

                {/* Service Name */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm">
                    {config.entity_type_display}
                  </p>
                  <p className="text-xs text-gray-400 truncate">{config.odata_path}</p>
                </div>

                {/* Status */}
                <div className="w-24 shrink-0">
                  <StatusBadge status={config.last_sync_status} />
                </div>

                {/* Last Sync */}
                <div className="w-20 shrink-0 text-xs text-gray-600">
                  {timeAgo(config.last_synced_at)}
                </div>

                {/* Enabled Toggle */}
                <div className="w-16 shrink-0 flex justify-center" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() =>
                      toggleMutation.mutate({
                        id: config.id,
                        enabled: !config.is_enabled,
                      })
                    }
                    className="text-gray-500 hover:text-gray-700"
                    title={config.is_enabled ? 'Disable' : 'Enable'}
                  >
                    {config.is_enabled ? (
                      <ToggleRight className="w-6 h-6 text-green-500" />
                    ) : (
                      <ToggleLeft className="w-6 h-6 text-gray-400" />
                    )}
                  </button>
                </div>

                {/* Actions */}
                <div className="w-32 shrink-0 flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => handleViewData(config)}
                    className="p-1.5 text-purple-600 hover:bg-purple-50 rounded"
                    title="View Data"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => openEditModal(config)}
                    className="p-1.5 text-amber-600 hover:bg-amber-50 rounded"
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => syncNowMutation.mutate({ id: config.id, delta: false })}
                    disabled={!config.is_enabled || syncingIds.has(config.id)}
                    className="p-1.5 text-cyan-600 hover:bg-cyan-50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Sync Now"
                  >
                    {syncingIds.has(config.id) ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Delete sync service "${config.entity_type_display}"?`)) {
                        deleteMutation.mutate(config.id);
                      }
                    }}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Expanded Detail */}
              {expandedRows.has(config.id) && (
                <ExpandedDetail config={config} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Sync Feedback Toast */}
      {syncFeedback && (
        <div
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
            syncFeedback.type === 'success'
              ? 'bg-green-600 text-white'
              : 'bg-red-600 text-white'
          }`}
        >
          {syncFeedback.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4" />
          ) : (
            <XCircle className="w-4 h-4" />
          )}
          {syncFeedback.message}
        </div>
      )}

      {/* Register Modal */}
      {showRegisterModal && selectedConnectionId && (
        <RegisterModal
          connectionId={selectedConnectionId}
          availableServices={unregisteredServices}
          onClose={() => {
            setShowRegisterModal(false);
            setRegisterTypes(new Set());
          }}
          onRegister={(types) => {
            registerMutation.mutate({
              connectionId: selectedConnectionId,
              types,
            });
          }}
          isPending={registerMutation.isPending}
        />
      )}

      {/* View Data Modal */}
      {viewingConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-[90vw] max-w-6xl max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Data: {viewingConfig.entity_type_display}
                </h3>
                <p className="text-sm text-gray-500">
                  {viewDataTotal} records
                  {viewDataTotal > 5000 && ' (showing first 5000)'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleViewData(viewingConfig)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                  title="Refresh"
                >
                  <RefreshCw className={`w-4 h-4 ${viewDataLoading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => {
                    setViewingConfig(null);
                    setViewData(null);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto">
              {viewDataLoading ? (
                <div className="flex items-center justify-center h-48">
                  <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
                </div>
              ) : viewData && viewData.length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      {Object.keys(viewData[0]).map((key) => (
                        <th
                          key={key}
                          className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap border-b"
                        >
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {viewData.map((record, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        {Object.values(record).map((value, colIdx) => (
                          <td
                            key={colIdx}
                            className="px-3 py-2 text-gray-700 whitespace-nowrap max-w-[300px] truncate"
                            title={value === null ? 'null' : String(value)}
                          >
                            {value === null ? (
                              <span className="text-gray-400 italic">null</span>
                            ) : typeof value === 'object' ? (
                              JSON.stringify(value)
                            ) : (
                              String(value)
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                  <Database className="w-10 h-10 mb-2" />
                  <p>No data found</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-3 border-t bg-gray-50 rounded-b-xl">
              <span className="text-sm text-gray-500">
                {viewData ? `Showing ${viewData.length} of ${viewDataTotal} records` : ''}
              </span>
              <button
                onClick={() => {
                  setViewingConfig(null);
                  setViewData(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Edit: {editingConfig.entity_type_display}
              </h3>
              <button
                onClick={() => setEditingConfig(null)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">OData Path</label>
                <input
                  type="text"
                  value={editForm.odata_path}
                  onChange={(e) => setEditForm({ ...editForm, odata_path: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm font-mono focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="/odata/namespace/EntitySet"
                />
                <p className="text-xs text-gray-400 mt-1">e.g. /odata/tcapi.ibdItem/Items (without /LN/lnapi prefix)</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Batch Size</label>
                  <input
                    type="number"
                    value={editForm.batch_size}
                    onChange={(e) => setEditForm({ ...editForm, batch_size: parseInt(e.target.value) || 500 })}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    min={1}
                    max={10000}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Auto Sync (minutes)</label>
                  <input
                    type="number"
                    value={editForm.sync_interval_minutes}
                    onChange={(e) => setEditForm({ ...editForm, sync_interval_minutes: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    min={0}
                    placeholder="0 = manual only"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editForm.delta_sync_enabled}
                    onChange={(e) => setEditForm({ ...editForm, delta_sync_enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500" />
                </label>
                <span className="text-sm text-gray-700">Delta Sync Enabled</span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setEditingConfig(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  updateMutation.mutate({
                    id: editingConfig.id,
                    updates: editForm,
                  });
                }}
                disabled={updateMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#45bdd6] rounded-lg hover:bg-[#3aa8bf] disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Expanded Detail Row
// ============================================================================

function ExpandedDetail({ config }: { config: SyncServiceConfig }) {
  return (
    <div className="px-4 pb-4 pt-1 ml-8 bg-gray-50/50">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        {/* Config Details */}
        <div>
          <p className="text-xs text-gray-500 mb-1">OData Path</p>
          <p className="font-mono text-xs text-gray-700 break-all">
            {config.odata_path || '—'}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Batch Size</p>
          <p className="text-gray-700">{config.batch_size}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Delta Sync</p>
          <p className="text-gray-700">
            {config.delta_sync_enabled ? 'Enabled' : 'Disabled'}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Auto Sync</p>
          <p className="text-gray-700">
            {config.sync_interval_minutes > 0
              ? `Every ${config.sync_interval_minutes} min`
              : 'Manual only'}
          </p>
        </div>

        {/* Dates */}
        <div>
          <p className="text-xs text-gray-500 mb-1">Created</p>
          <p className="text-gray-700">
            {new Date(config.created_at).toLocaleDateString('en-US', {
              year: 'numeric', month: 'short', day: 'numeric',
            })}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Last Updated</p>
          <p className="text-gray-700">
            {new Date(config.updated_at).toLocaleDateString('en-US', {
              year: 'numeric', month: 'short', day: 'numeric',
            })}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Connection</p>
          <p className="text-gray-700">{config.connection_name}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Service ID</p>
          <p className="font-mono text-xs text-gray-400 break-all">{config.id}</p>
        </div>
      </div>

      {/* Error Message */}
      {config.last_error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-xs font-medium text-red-700 mb-1">Last Error</p>
          <p className="text-xs text-red-600 font-mono whitespace-pre-wrap">
            {config.last_error}
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Register Modal
// ============================================================================

function RegisterModal({
  connectionId,
  availableServices,
  onClose,
  onRegister,
  isPending,
}: {
  connectionId: string;
  availableServices: AvailableSyncService[];
  onClose: () => void;
  onRegister: (types: string[]) => void;
  isPending: boolean;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggleAll = () => {
    if (selected.size === availableServices.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(availableServices.map((s) => s.entity_type)));
    }
  };

  const toggle = (type: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Register Sync Services</h3>
          <p className="text-sm text-gray-500 mt-1">
            Select which services to register for this connection
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {availableServices.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              All available services are already registered
            </p>
          ) : (
            <>
              {/* Select All */}
              <label className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-100 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.size === availableServices.length}
                  onChange={toggleAll}
                  className="w-4 h-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Select All ({availableServices.length})
                </span>
              </label>

              {/* Service List */}
              <div className="space-y-2">
                {availableServices.map((service) => (
                  <label
                    key={service.entity_type}
                    className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(service.entity_type)}
                      onChange={() => toggle(service.entity_type)}
                      className="w-4 h-4 mt-0.5 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {service.entity_type_display}
                      </p>
                      <p className="text-xs text-gray-400 font-mono truncate">
                        {service.odata_path}
                      </p>
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      Batch: {service.batch_size}
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onRegister(Array.from(selected))}
            disabled={selected.size === 0 || isPending}
            className="px-4 py-2 text-sm bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? 'Registering...' : `Register ${selected.size} Service${selected.size !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
